import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, tenantAPI, AppUser, Tenant } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import Icon, { type IconName } from '../components/Icon';
import Empty from '../components/ui/Empty';
import { fmtDate } from '../utils/format';

/**
 * Super Admin / Tenant Admin: user management.
 *  - super_admin: manage users across all tenants (and assign any role)
 *  - tenant_admin: manage users within their own tenant only
 */

const AV_BG = ['var(--brand-500)', 'var(--ch-sms)', 'var(--ch-viber)', 'var(--ch-email)'];
const avColor = (key: string) => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AV_BG[h % AV_BG.length];
};
const initials = (name: string) => name.trim().slice(0, 2).toUpperCase() || '?';

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const tr = useT();
  const isSuperAdmin = role === 'super_admin';

  const [users, setUsers] = useState<AppUser[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [menu, setMenu] = useState<number | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user' as 'super_admin' | 'tenant_admin' | 'user',
    tenantId: '' as string | number,
  });

  const menuRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    loadUsers();
    if (isSuperAdmin) loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDoc = () => setMenu(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await userAPI.getAll();
      if (res.data.success) setUsers(res.data.data.users);
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Učitavanje korisnika nije uspelo', 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await tenantAPI.getAll();
      if (res.data.success) setTenants(res.data.data.tenants);
    } catch {
      /* ignore */
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const payload: any = {
        username: form.username,
        email: form.email,
        password: form.password,
        role: form.role,
      };
      if (form.role !== 'super_admin') {
        payload.tenantId = form.tenantId ? Number(form.tenantId) : undefined;
      }
      const res = await userAPI.create(payload);
      if (res.data.success) {
        setSuccess(tr(`Korisnik „${res.data.data.user.username}" je kreiran.`, `User "${res.data.data.user.username}" created.`));
        setForm({ username: '', email: '', password: '', role: 'user', tenantId: '' });
        setShowCreate(false);
        loadUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Kreiranje korisnika nije uspelo', 'Failed to create user'));
    }
  };

  const handleToggleActive = async (u: AppUser) => {
    setError('');
    try {
      await userAPI.update(u.id, { isActive: !u.isActive });
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Ažuriranje korisnika nije uspelo', 'Failed to update user'));
    }
  };

  const handleResetPassword = async (u: AppUser) => {
    const pwd = window.prompt(tr(`Nova lozinka za „${u.username}" (min 6 karaktera):`, `New password for "${u.username}" (min 6 chars):`));
    if (!pwd) return;
    setError('');
    setSuccess('');
    try {
      await userAPI.resetPassword(u.id, pwd);
      setSuccess(tr(`Lozinka je ažurirana za „${u.username}".`, `Password updated for "${u.username}".`));
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Resetovanje lozinke nije uspelo', 'Failed to reset password'));
    }
  };

  const handleDelete = async (u: AppUser) => {
    if (!window.confirm(tr(`Obrisati korisnika „${u.username}"? Ovo se ne može opozvati.`, `Delete user "${u.username}"? This cannot be undone.`))) return;
    setError('');
    setSuccess('');
    try {
      await userAPI.remove(u.id);
      setSuccess(tr(`Korisnik „${u.username}" je obrisan.`, `User "${u.username}" deleted.`));
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Brisanje korisnika nije uspelo', 'Failed to delete user'));
    }
  };

  if (role !== 'super_admin' && role !== 'tenant_admin') {
    return (
      <div className="page page-wide">
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)' }}>
          <strong>{tr('Pristup odbijen.', 'Access denied.')}</strong> {tr('Ova stranica je namenjena samo administratorima.', 'This page is for administrators only.')}
        </div>
      </div>
    );
  }

  const roleBadge = (r: string) => {
    const m: Record<string, [string, string, string]> = {
      super_admin: ['var(--ch-viber-soft)', 'var(--ch-viber)', tr('Super Admin', 'Super Admin')],
      tenant_admin: ['var(--ch-sms-soft)', 'var(--ch-sms)', tr('Admin klijenta', 'Tenant Admin')],
      user: ['var(--surface-2)', 'var(--text-2)', tr('Korisnik', 'User')],
    };
    const [bg, c, label] = m[r] || ['var(--surface-2)', 'var(--text-2)', r];
    return (
      <span className="chip" style={{ background: bg, color: c, border: 'none' }}>
        {label}
      </span>
    );
  };

  return (
    <div className="page page-wide">
      <div className="page-head">
        <div>
          <div className="page-title">{tr('Korisnici', 'Users')}</div>
          <div className="page-sub">{tr('Upravljanje korisnicima i njihovim ulogama.', 'Manage users and their roles.')}</div>
        </div>
        <div className="flex items-center gap-12">
          {isSuperAdmin && (
            <button className="btn btn-secondary" onClick={() => navigate('/admin/tenants')}>
              <Icon name="building" size={16} /> {tr('Klijenti', 'Tenants')}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Icon name="plus" size={16} /> {tr('Novi korisnik', 'New user')}
          </button>
        </div>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 16 }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="card card-pad" style={{ borderColor: 'var(--brand-500)', color: 'var(--brand-700)', marginBottom: 16 }}>
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="card-pad muted">{tr('Učitavanje…', 'Loading…')}</div>
        ) : users.length === 0 ? (
          <Empty
            icon="users"
            title={tr('Nema korisnika', 'No users')}
            sub={tr('Kreiraj prvog korisnika da biste počeli.', 'Create your first user to get started.')}
            action={
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                <Icon name="plus" size={16} /> {tr('Novi korisnik', 'New user')}
              </button>
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>{tr('Korisnik', 'User')}</th>
                <th>{tr('Uloga', 'Role')}</th>
                {isSuperAdmin && <th>{tr('Klijent', 'Tenant')}</th>}
                <th>{tr('Poslednja prijava', 'Last login')}</th>
                <th>{tr('Aktivan', 'Active')}</th>
                <th />
              </tr>
            </thead>
            <tbody ref={menuRef}>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ paddingLeft: 22 }}>
                    <div className="flex items-center gap-12">
                      <span className="av-mini" style={{ background: avColor(u.username) }}>
                        {initials(u.username)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{u.username}</div>
                        <div className="dim" style={{ fontSize: 12.5 }}>
                          {u.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{roleBadge(u.role)}</td>
                  {isSuperAdmin && (
                    <td className="muted" style={{ fontSize: 13.5 }}>
                      {u.tenant ? u.tenant.name : u.tenantId ?? '—'}
                    </td>
                  )}
                  <td className="muted" style={{ fontSize: 13 }}>
                    {u.lastLoginAt ? fmtDate(u.lastLoginAt) : '—'}
                  </td>
                  <td>
                    <button
                      className={`switch ${u.isActive ? 'on' : ''}`}
                      aria-label={u.isActive ? tr('Deaktiviraj', 'Deactivate') : tr('Aktiviraj', 'Activate')}
                      title={u.isActive ? tr('Aktivan', 'Active') : tr('Neaktivan', 'Inactive')}
                      onClick={() => handleToggleActive(u)}
                    />
                  </td>
                  <td style={{ position: 'relative', textAlign: 'right', paddingRight: 18 }}>
                    <button
                      className="icon-btn"
                      aria-label={tr('Akcije', 'Actions')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenu(menu === u.id ? null : u.id);
                      }}
                    >
                      <Icon name="more" size={18} />
                    </button>
                    {menu === u.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          right: 18,
                          top: 44,
                          zIndex: 20,
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--r-md)',
                          boxShadow: 'var(--sh-lg)',
                          padding: 6,
                          width: 200,
                          textAlign: 'left',
                        }}
                      >
                        <MenuItem
                          icon="edit"
                          label={u.isActive ? tr('Deaktiviraj', 'Deactivate') : tr('Aktiviraj', 'Activate')}
                          onClick={() => {
                            setMenu(null);
                            handleToggleActive(u);
                          }}
                        />
                        <MenuItem
                          icon="shield"
                          label={tr('Resetuj lozinku', 'Reset password')}
                          onClick={() => {
                            setMenu(null);
                            handleResetPassword(u);
                          }}
                        />
                        <MenuItem
                          icon="trash"
                          label={tr('Obriši', 'Delete')}
                          danger
                          onClick={() => {
                            setMenu(null);
                            handleDelete(u);
                          }}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <div className="overlay" onClick={() => setShowCreate(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={handleCreate}>
            <div className="modal-head">
              <div className="card-title">{tr('Novi korisnik', 'New user')}</div>
              <button type="button" className="icon-btn" aria-label={tr('Zatvori', 'Close')} onClick={() => setShowCreate(false)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="grid-2">
                <div className="field">
                  <label className="label">{tr('Korisničko ime *', 'Username *')}</label>
                  <input
                    className="input"
                    required
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="jsmith"
                    autoComplete="off"
                  />
                </div>
                <div className="field">
                  <label className="label">{tr('Email *', 'Email *')}</label>
                  <input
                    className="input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jsmith@acme.com"
                    autoComplete="off"
                  />
                </div>
                <div className="field">
                  <label className="label">{tr('Lozinka *', 'Password *')}</label>
                  <input
                    className="input"
                    type="password"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={tr('min 6 karaktera', 'min 6 characters')}
                    autoComplete="new-password"
                  />
                </div>
                <div className="field">
                  <label className="label">{tr('Uloga', 'Role')}</label>
                  <select
                    className="select"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value as any })}
                  >
                    <option value="user">{tr('Korisnik', 'User')}</option>
                    <option value="tenant_admin">{tr('Admin klijenta', 'Tenant Admin')}</option>
                    {isSuperAdmin && <option value="super_admin">{tr('Super Admin', 'Super Admin')}</option>}
                  </select>
                </div>
                {isSuperAdmin && form.role !== 'super_admin' && (
                  <div className="field">
                    <label className="label">{tr('Klijent *', 'Tenant *')}</label>
                    <select
                      className="select"
                      value={form.tenantId}
                      onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
                      required
                    >
                      <option value="">{tr('— izaberi klijenta —', '— select tenant —')}</option>
                      {tenants.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.slug})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                {tr('Otkaži', 'Cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {tr('Kreiraj', 'Create')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{ icon: IconName; label: string; onClick: () => void; danger?: boolean }> = ({
  icon,
  label,
  onClick,
  danger,
}) => (
  <button
    className="lrow"
    style={{
      width: '100%',
      padding: '9px 11px',
      border: 'none',
      background: 'transparent',
      fontWeight: 600,
      fontSize: 13.5,
      color: danger ? 'var(--danger)' : 'var(--text)',
      gap: 10,
      cursor: 'pointer',
    }}
    onClick={onClick}
  >
    <Icon name={icon} size={16} /> {label}
  </button>
);

export default UserList;
